import { useState, useEffect } from 'react';
import {
  $getSelection,
  $isRangeSelection,
  KEY_DOWN_COMMAND,
  COMMAND_PRIORITY_LOW,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from 'lexical';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListItemNode,
  $isListNode,
} from '@lexical/list';
import { useRichTextEditor } from '../RichTextEditorProvider';
import { IconButton, Tooltip } from '@sweep-io/sweep-design';
import { ListBullets } from '@sweep-io/sweep-design/dist/icons';

export const BulletListButton = () => {
  const { editor } = useRichTextEditor();
  const [isBulletList, setIsBulletList] = useState(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }: any) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const element =
            anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();

          const isInList = $isListNode(element) || $isListItemNode(element);
          setIsBulletList(isInList);
        }
      });
    });
  }, [editor]);

  // Register Tab and Shift+Tab keyboard handlers for list indentation
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        // Only handle Tab key when inside a bullet list
        if (event.key === 'Tab' && isBulletList) {
          event.preventDefault();

          if (event.shiftKey) {
            // Shift+Tab: Outdent the list item
            editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
          } else {
            // Tab: Indent the list item
            editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
          }

          return true; // Event handled
        }

        return false; // Event not handled
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, isBulletList]);

  return (
    <Tooltip title="Bullet List (Ctrl+Shift+8)">
      <IconButton
        variant="squared"
        size="small"
        onClick={() => {
          if (isBulletList) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          }
        }}
        pressed={isBulletList}
      >
        <ListBullets />
      </IconButton>
    </Tooltip>
  );
};
